import type {
  BoardUnit,
  ShopSlot,
  ShopState,
  Star,
  UnitDef,
} from '@origami/shared'

/**
 * Build a "round 4" mock shop state from the unit catalog. Used by
 * ShopTestScreen to drive the visual without a real store / server
 * loop. Picks units deterministically from whatever's in the catalog
 * so the layout looks reasonable with any roster size.
 */
export function buildMockShopState(catalog: UnitDef[]): ShopState {
  if (catalog.length === 0) {
    return emptyMockState()
  }

  const byTier = (t: number) => catalog.filter((u) => u.tier === t)
  const pick = (t: number, idx = 0): UnitDef | null => {
    const pool = byTier(t)
    return pool[idx % pool.length] ?? null
  }
  const pickAny = (idx: number): UnitDef =>
    catalog[idx % catalog.length]!

  // Drafting table — 5 slots, mostly low tier per spec §5 distribution.
  const shopSlots: ShopSlot[] = [
    pick(1, 0),
    pick(1, 1),
    pick(2, 0),
    pick(2, 1),
    pick(3, 0),
  ]
    .filter((u): u is UnitDef => u !== null)
    .map((unit) => ({ unit, cost: unit.tier }))

  // Pad up to 5 if some tiers were empty.
  while (shopSlots.length < 5) {
    const u = pickAny(shopSlots.length)
    shopSlots.push({ unit: u, cost: u.tier })
  }
  // Mark the third slot bought to demo the "PURCHASED" overlay.
  if (shopSlots[2]) shopSlots[2].bought = true

  // Bonus slot — one tier above current shop level (per spec §5),
  // grant from a recent star-up combine. Pick highest-tier unit
  // available so it reads as "premium".
  const bonus = pick(4, 0) ?? pick(3, 1) ?? pickAny(7)
  shopSlots.push({ unit: bonus, cost: 0, isBonus: true })

  // Active line — front (1) → back (5). Mix of stars + fill states
  // so the BoardSlot variants exercise all three star tiers
  // (★1 fresh, ★2 ascended, ★3 fully evolved).
  const activeLine: (BoardUnit | null)[] = [
    boardUnit(pickAny(0), 3, 3, +4, +3),
    boardUnit(pickAny(1), 1, 2, +0, +0),
    boardUnit(pickAny(0), 2, 1, +1, +1),
    boardUnit(pickAny(2), 1, 1, +0, +0),
    null,
  ]

  // Bench — three units resting. Mixes star tiers, and mirrors two
  // active-line units so the harness can exercise BOTH combine
  // animations: bench[0] + active[1] (fill 2 + 2 → STAR UP ceremony)
  // and bench[2] + active[3] (fill 1 + 1 → in-place fill tick).
  const bench: (BoardUnit | null)[] = [
    boardUnit(pickAny(1), 1, 2, +0, +0),
    boardUnit(pickAny(4), 3, 3, +3, +2),
    boardUnit(pickAny(2), 1, 1, 0, 0),
  ]

  return {
    round: 4,
    gold: 50,
    health: 22,
    maxHealth: 30,
    wins: 2,
    maxWins: 10,
    shopLevel: 3,
    shopSlots,
    activeLine,
    bench,
    rerollCost: 1,
  }
}

function boardUnit(
  unit: UnitDef,
  star: Star,
  fill: 0 | 1 | 2 | 3,
  hpDelta: number,
  atkDelta: number,
): BoardUnit {
  return {
    unit,
    star,
    fill,
    hp: unit.stats.hp + hpDelta,
    atk: unit.stats.atk + atkDelta,
  }
}

function emptyMockState(): ShopState {
  return {
    round: 1,
    gold: 3,
    health: 30,
    maxHealth: 30,
    wins: 0,
    maxWins: 10,
    shopLevel: 1,
    shopSlots: [],
    activeLine: [null, null, null, null, null],
    bench: [null, null, null],
    rerollCost: 1,
  }
}
